/**
 * Sidebar — barre de navigation latérale UNIQUE.
 * Fond blanc, icônes or, texte noir doux, item actif surligné en or pâle.
 * Inclut un badge "plan" et un lien vers Pricing/Billing.
 */
import type { JSX } from 'react';
import {
  Sunrise,
  LayoutGrid,
  Timer,
  Inbox,
  FolderOpen,
  BarChart3,
  Settings,
  CreditCard,
  Lock,
  LogOut,
} from 'lucide-react';
import { useNavStore, type Route } from '@store/navStore';
import { useUserStore } from '@store/userStore';
import { usePlan } from '@/hooks/usePlan';
import { PLAN_LABELS } from '@/lib/plans';

interface Item {
  id: Route;
  label: string;
  icon: JSX.Element;
  lockedOn?: 'essential' | 'pro'; // verrouillé si plan insuffisant
}

const ITEMS: Item[] = [
  { id: 'briefing',  label: 'Briefing',        icon: <Sunrise size={18} /> },
  { id: 'dashboard', label: 'Tableau de bord',  icon: <LayoutGrid size={18} /> },
  { id: 'focus',     label: 'Focus Work',       icon: <Timer size={18} />,     lockedOn: 'essential' },
  { id: 'commhub',   label: 'Communication',    icon: <Inbox size={18} /> },
  { id: 'smartsave', label: 'SmartSave',        icon: <FolderOpen size={18} />, lockedOn: 'essential' },
  { id: 'analytics', label: 'Analytics',        icon: <BarChart3 size={18} /> },
  { id: 'settings',  label: 'Paramètres',       icon: <Settings size={18} /> },
];

export function Sidebar(): JSX.Element {
  const route    = useNavStore((s) => s.route);
  const navigate = useNavStore((s) => s.navigate);
  const logout   = useUserStore((s) => s.logout);
  const profile  = useUserStore((s) => s.profile);
  const { plan, isEssential } = usePlan();

  return (
    <aside
      className="hidden md:flex flex-col bg-white border-r border-[color:var(--color-border)] w-56 py-6 px-3 sticky top-[60px] self-start"
      style={{ height: 'calc(100vh - 60px)' }}
      aria-label="Navigation principale"
    >
      <nav className="flex flex-col gap-1 flex-1">
        {ITEMS.map((item) => {
          const active  = route === item.id;
          const locked  = item.lockedOn === 'essential' && !isEssential;

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(item.id)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-left transition-unique"
              style={{
                background: active ? 'var(--color-primary-light)' : 'transparent',
                color: locked ? 'var(--color-text-medium)' : 'var(--color-text-dark)',
                borderLeft: active
                  ? '3px solid var(--color-primary-dark)'
                  : '3px solid transparent',
              }}
              aria-current={active ? 'page' : undefined}
              title={locked ? `Disponible à partir du plan Essentiel` : undefined}
            >
              <span
                style={{
                  color: locked
                    ? 'var(--color-text-medium)'
                    : active
                    ? 'var(--color-primary-dark)'
                    : 'var(--color-primary)',
                }}
              >
                {item.icon}
              </span>
              {item.label}
              {locked && <Lock size={11} className="ml-auto" style={{ color: 'var(--color-text-medium)' }} />}
            </button>
          );
        })}
      </nav>

      {/* Zone abonnement en bas */}
      <div
        className="mt-auto pt-4 border-t flex flex-col gap-1"
        style={{ borderColor: 'var(--color-border)' }}
      >
        {/* Badge plan */}
        <button
          type="button"
          onClick={() => navigate('billing')}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-unique hover:bg-[color:var(--color-primary-light)]"
          style={{ color: 'var(--color-primary-dark)' }}
        >
          <CreditCard size={14} />
          <span>Plan {PLAN_LABELS[plan]}</span>
          {plan === 'free' && (
            <span
              className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold"
              style={{ background: 'var(--color-primary)', color: 'var(--color-text-dark)' }}
            >
              Upgrader
            </span>
          )}
        </button>

        {/* Déconnexion */}
        <button
          type="button"
          onClick={logout}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs transition-unique hover:bg-red-50"
          style={{ color: 'var(--color-text-medium)' }}
        >
          <LogOut size={14} />
          <span>Déconnexion</span>
          {profile && (
            <span className="ml-auto truncate max-w-[80px] opacity-60 text-[10px]">
              {profile.firstName}
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}
